import { useLocation, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@utils/utils";
import { Beta } from "../Beta";

interface BreadcrumbProps {
  className?: string;
  alertTitle?: string;
}

const routeLabels: Record<string, { label: string; path: string }> = {
  "alert-details": { label: "Detalles de alerta", path: "" },
  "communication": { label: "Comunicación", path: "/communication" },
  "notifications": { label: "Notificaciones", path: "/notifications" },
  "condiciones-meteorologicas": {
    label: "Condiciones Meteorológicas",
    path: "/observatory/condiciones-meteorologicas",
  },
  "create-forecast": {
    label: "Crear pronóstico",
    path: "/communication/create-forecast",
  },
  "create-multiple-forecast": {
    label: "Crear pronóstico múltiple",
    path: "/communication/create-multiple-forecast",
  },
  "create-user": { label: "Crear usuario", path: "/users/create-user" },
  "drill": { label: "Crear simulacro", path: "/communication/phenomenon/drill" },
  "eventos-tormenta": {
    label: "Eventos de Lluvia",
    path: "/observatory/eventos-tormenta",
  },
  "flood": { label: "Inundación", path: "/communication/phenomenon/flood" },
  "forecast": { label: "Pronóstico", path: "/communication/create-forecast" },
  "home": { label: "Home", path: "/home" },
  "list-alerts": {
    label: "Lista de alertas",
    path: "/communication/list-alerts",
  },
  "monitor": { label: "Monitor", path: "/monitor" },
  "info": { label: "Notificaciones informativas", path: "/communication/info" },
  "general": { label: "Informativas Generales", path: "/communication/info/general" },
  "traffic": { label: "Informativas de Tráfico", path: "/communication/info/traffic" },
  "security": { label: "Informativas de Seguridad", path: "/communication/info/security" },
  "news": { label: "Notificaciones de noticias", path: "/communication/list-alerts/new" },
  "observatory": { label: "Observatorio", path: "/observatory" },
  "phenomenon": { label: "Fenómeno", path: "/communication/phenomenon" },
  "report": { label: "Reportes", path: "/report" },
  "storm": { label: "Lluvia", path: "/communication/phenomenon/storm" },
  "storm,flood,temp_flood,wind,drill,frost,tropical_cyclone,forest_fire,volcanic,low_temperatures,earthquake,tsunami,high_temperatures,environmental_risk,hail,dust_devil": { label: "Historico fenómenos", path: "/communication/lists-alerts/storm,flood,temp_flood,wind,drill,frost,tropical_cyclone,forest_fire,volcanic,low_temperatures,earthquake,tsunami,high_temperatures,environmental_risk,hail,dust_devil" },
  "temp_flood": { label: "Inundación", path: "/communication/phenomenon/flood" },
  "users": { label: "Usuarios", path: "/users" },
  "wind": { label: "Vientos", path: "/communication/phenomenon/wind" },
  "frost": { label: "Heladas", path: "/communication/phenomenon/frost" },
  "tropical_cyclone": { label: "Ciclones tropicales", path: "/communication/phenomenon/tropical-cyclone" },
  "forest_fire": { label: "Incendios forestales", path: "/communication/phenomenon/forest_fire" },
  "volcanic": { label: "Actividad volcánica", path: "/communication/phenomenon/volcanic" },
  "low_temperatures": { label: "Bajas temperaturas", path: "/communication/phenomenon/low_temperatures" },
  "environmental_risk": { label: "Contingencia atmosférica", path: "/communication/phenomenon/environmental_risk" },
  "hail": { label: "Granizo", path: "/communication/phenomenon/hail" },
  "dust_devil": { label: "Tolvanera", path: "/communication/phenomenon/dust_devil" },
  "analytics": { label: "Reportes EDL", path: "/analytics" },
  "demographic-graphs": { label: "Reportes App Jalisco alerta", path: "/analytics/demographic-graphs" },
};

export const Breadcrumb = ({ className, alertTitle }: BreadcrumbProps) => {
  const location = useLocation();
  const paths = location.pathname.split("/").filter(Boolean);

  if (paths.length === 0) return null;

  const buildPath = (index: number) => {
    return "/" + paths.slice(0, index + 1).join("/");
  };

  return (
    <div
      className={cn(
        "ml-6 bg-white/40 backdrop-blur-md rounded-2xl p-2 shadow-sm z-10 border border-white/20 w-fit",
        className
      )}
    >
      <nav
        className="bg-white/40 backdrop-blur-sm rounded-xl px-4 py-2 inline-block"
        aria-label="Breadcrumb"
      >
        <ol className="flex items-center space-x-2 text-sm">
          <li className="text-gray-white">
            <Link
              to="/home"
              className="hover:text-foreground transition-colors"
            >
              Home
            </Link>
          </li>
          {paths.map((path, index) => {
            const isLastPath = index === paths.length - 1;
            const routeInfo = routeLabels[path];
            const displayText =
              alertTitle && path === "alert-details"
                ? alertTitle
                : routeInfo?.label || path;
            const pathTo = routeInfo?.path || buildPath(index);

            return (
              <li key={index} className="flex items-center space-x-2">
                <ChevronRight className="h-4 w-4 text-gray-400" />
                {isLastPath ? (
                  <span className="flex items-center gap-2 text-foreground font-medium">
                    {displayText}
                    {path === "create-multiple-forecast" && <Beta />}
                  </span>
                ) : (
                  <Link
                    to={pathTo}
                    className="text-gray-600 hover:text-foreground transition-colors"
                  >
                    {displayText}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};
